import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TpETLComponentTypeManifest } from '../interfaces';
import { TpETLPipelineComponent, TpETLDryRun, TpETLDryRunComponentResult } from '../../interfaces';
import { TpETLObjectTypes } from '../../etl-data-types/interfaces';
import {
  createConfigParamTypeExistingColumn,
  createConfigParamTypeExistingColumnList,
  ETLDryRunObjectTableAddColumn,
} from '../utils';
import {
  addDryRunObjectTable,
  getETLComponentInputObjectTable,
  getETLTableObjectColumn,
  getETLTableObjectColumnDataTypeIfPresent,
} from '../../utils';
import { get_guid } from '../../../visual-analytics/helpers/misc';

import styles from '../../edit-etl-component/editETLComponentForm.module.scss';
import {
  aggregationHasCompatibleSourceColumn,
  aggregationTypesList,
  getAggregationTypeInfo,
} from '../../aggregations/aggregationTypes';
import { addETLDryRunError, getDryRunErrorsForComponent } from '../../etlDryRun';
import ParamSet from '../../edit-etl-component/ParamSet';
import { FancyButton } from '../../../../components/primitives/FancyButton';
import { CONFIGPARAMTYPES } from '../../../visual-analytics/helpers/configParams';

export interface TpPivotColumnInfo {
  id: string; // unique identifier of this pivot
  destColId: string;
  destColName: string;
  destColDescription: string;
  filterValue: string;
}

function performDryRun(dryRun: TpETLDryRun, comp: TpETLPipelineComponent): TpETLDryRunComponentResult {
  const inputTableObj = getETLComponentInputObjectTable(dryRun, comp, 'sourceTable');
  const outputTableObj = addDryRunObjectTable(dryRun, comp.outputObjectIds['result'], comp);

  try {
    const colIdList = (comp.params['groupByColumns'] || []) as string[];
    if (colIdList.length == 0) throw `Group by column(s) not specified`;
    for (const colId of colIdList) {
      const sourceColInfo = getETLTableObjectColumn(inputTableObj, colId);
      ETLDryRunObjectTableAddColumn(
        outputTableObj,
        sourceColInfo.identifier,
        sourceColInfo.name,
        sourceColInfo.description,
        sourceColInfo.dataType,
      );
    }
  } catch (error) {
    addETLDryRunError(dryRun, String(error), comp.id, 'groupByColumns');
  }

  if (!comp.params['pivotColumn']) {
    addETLDryRunError(dryRun, 'Pivot column not specified', comp.id, 'pivotColumn');
    return { abort: false };
  }
  const pivotSourceColInfo = getETLTableObjectColumn(inputTableObj, comp.params['pivotColumn']);

  if (!comp.params['cellValuesColumn']) {
    addETLDryRunError(dryRun, 'Cell values column not specified', comp.id, 'cellValuesColumn');
    return { abort: false };
  }
  if (!comp.params['aggType']) {
    addETLDryRunError(dryRun, 'Aggregation type not specified', comp.id, 'aggType');
    return { abort: false };
  }
  const cellColInfo = getETLTableObjectColumn(inputTableObj, comp.params['cellValuesColumn']);
  const aggTypeInfo = getAggregationTypeInfo(comp.params['aggType']);
  // aggTypeInfo.performDryRun({ dryRun, comp, agg })
  if (!aggTypeInfo) {
    addETLDryRunError(dryRun, `Invalid aggregation type: ${comp.params['aggType']}`, comp.id, 'aggType');
    return { abort: false };
  }
  ETLDryRunObjectTableAddColumn(
    outputTableObj,
    cellColInfo.identifier,
    cellColInfo.name,
    cellColInfo.description,
    aggTypeInfo.outputDataType || cellColInfo.dataType,
  );

  for (const pivotColumn of (comp.params['pivotColumns'] || []) as TpPivotColumnInfo[]) {
    try {
      if (!pivotColumn.filterValue)
        addETLDryRunError(dryRun, `Pivot output column has no filter value`, comp.id, pivotColumn.id);
      if (!pivotColumn.destColId)
        addETLDryRunError(dryRun, `Pivot output column has no identifier`, comp.id, pivotColumn.id);

      if (pivotColumn.destColId) {
        ETLDryRunObjectTableAddColumn(
          outputTableObj,
          pivotColumn.destColId,
          pivotColumn.destColName || `${cellColInfo.name} - ${pivotColumn.filterValue}`,
          pivotColumn.destColDescription ||
            `${cellColInfo.description} (${pivotSourceColInfo.name} = ${pivotColumn.filterValue})`,
          aggTypeInfo.outputDataType || cellColInfo.dataType,
        );
      }
    } catch (error) {
      addETLDryRunError(dryRun, String(error), comp.id, pivotColumn.id);
    }
  }

  return { abort: false };
}

function renderCustomConfigParams(
  dryRun: TpETLDryRun,
  comp: TpETLPipelineComponent,
  updateEditingComponent: (newComp: TpETLPipelineComponent) => void,
): any {
  const sourceTable = getETLComponentInputObjectTable(dryRun, comp, 'sourceTable');

  const cellValuesColumn = comp.params['cellValuesColumn'];
  const aggType = comp.params['aggType'];

  const pivotColumns = (comp.params['pivotColumns'] || []) as TpPivotColumnInfo[];

  function renderAggregationTypeErrors() {
    const errors = getDryRunErrorsForComponent(dryRun, comp.id).filter((error) => error.paramId == 'aggType');
    if (errors.length == 0) return null;
    return (
      <div className={styles.errors}>
        {errors.map((error, idx) => (
          <div key={`errors_aggType_${idx}`}>{error.message}</div>
        ))}
      </div>
    );
  }

  function renderPivotColumnErrors(pivotCol: TpPivotColumnInfo) {
    const errors = getDryRunErrorsForComponent(dryRun, comp.id).filter((error) => error.paramId == pivotCol.id);
    if (errors.length == 0) return null;
    return (
      <div className={styles.errors}>
        {errors.map((error, idx) => (
          <div key={`errors_${pivotCol.id}_${idx}`}>{error.message}</div>
        ))}
      </div>
    );
  }

  function renderCustomAggregationParams(aggType: string) {
    if (!aggType) return;
    const aggTypeInfo = getAggregationTypeInfo(aggType);
    if (!aggTypeInfo.renderCustomParams) return null;
    const agg = comp.params['aggParams'] || aggTypeInfo.renderCustomParams;
    return (
      <>
        <h3>Custom parameters</h3>
        {aggTypeInfo.renderCustomParams({
          agg,
          sourceTable,
          updateAggregationParam: (paramId: string, paramValue: any) => {
            comp.params['aggParams'] = { ...comp.params['aggParams'], [paramId]: paramValue };
            updateEditingComponent(comp);
          },
        })}
      </>
    );
  }

  function updatePivotColumn(pivotColumnId: string, paramId: string, newValue: any) {
    const pivotColumnIdx = pivotColumns.map((pivotCol) => pivotCol.id).indexOf(pivotColumnId);
    if (pivotColumnIdx < 0) throw `Invalid pivot column id ${pivotColumnId}`;
    if (paramId == 'filterValue') pivotColumns[pivotColumnIdx].filterValue = newValue;
    else if (paramId == 'destColId') pivotColumns[pivotColumnIdx].destColId = newValue;
    else if (paramId == 'destColName') pivotColumns[pivotColumnIdx].destColName = newValue;
    else if (paramId == 'destColDescription') pivotColumns[pivotColumnIdx].destColDescription = newValue;
    updateEditingComponent(comp);
  }

  return (
    <>
      <div className={styles.section}>
        <h3>Cell Values Aggregation</h3>
        {renderAggregationTypeErrors()}
        <>
          <select
            value={aggType}
            onChange={(ev) => {
              const aggTypeId = ev.target.value;
              comp.params['aggType'] = aggTypeId;
              if (aggTypeId) {
                const aggTypeInfo = getAggregationTypeInfo(aggTypeId);
                comp.params['aggParams'] = { ...aggTypeInfo.defaultCustomParams };
              } else {
                comp.params['aggParams'] = {};
              }
              updateEditingComponent(comp);
            }}
          >
            <option key="_empty_" value={''}>
              - Select -
            </option>
            {cellValuesColumn &&
              aggregationTypesList
                .filter((aggType) =>
                  aggregationHasCompatibleSourceColumn(
                    aggType.id,
                    getETLTableObjectColumnDataTypeIfPresent(sourceTable, cellValuesColumn),
                  ),
                )
                .map((aggType) => (
                  <option key={aggType.id} value={aggType.id}>
                    {aggType.name}
                  </option>
                ))}
          </select>
          <div style={{ marginTop: '3px' }}>{aggType && getAggregationTypeInfo(aggType).description}</div>
        </>
        {renderCustomAggregationParams(aggType)}
      </div>

      {pivotColumns.map((pivotColumn) => (
        <div className={styles.section}>
          <h3>
            Pivot Column
            <button
              onClick={() => {
                comp.params['pivotColumns'] = pivotColumns.filter((filterCol) => pivotColumn.id !== filterCol.id);
                updateEditingComponent(comp);
              }}
            >
              <FontAwesomeIcon icon="xmark" />
            </button>
          </h3>
          {renderPivotColumnErrors(pivotColumn)}

          <ParamSet
            params={[
              {
                name: 'Filter value',
                component: (
                  <input
                    value={pivotColumn.filterValue}
                    onChange={(ev) => {
                      updatePivotColumn(pivotColumn.id, 'filterValue', ev.target.value);
                    }}
                  />
                ),
              },
              {
                name: 'Result column identifier',
                component: (
                  <input
                    value={pivotColumn.destColId}
                    onChange={(ev) => {
                      updatePivotColumn(pivotColumn.id, 'destColId', ev.target.value);
                    }}
                  />
                ),
              },
              {
                name: 'Result column name',
                component: (
                  <input
                    value={pivotColumn.destColName}
                    placeholder="Automatic"
                    onChange={(ev) => {
                      updatePivotColumn(pivotColumn.id, 'destColName', ev.target.value);
                    }}
                  />
                ),
              },
              {
                name: 'Result column description',
                component: (
                  <input
                    value={pivotColumn.destColDescription}
                    placeholder="Automatic"
                    onChange={(ev) => {
                      updatePivotColumn(pivotColumn.id, 'destColDescription', ev.target.value);
                    }}
                  />
                ),
              },
            ]}
          />
        </div>
      ))}

      <FancyButton
        iconName="fas:plus"
        onClick={() => {
          const newPivotColumn: TpPivotColumnInfo = {
            id: get_guid(),
            destColId: '',
            destColName: '',
            destColDescription: '',
            filterValue: '',
          };
          comp.params['pivotColumns'] = [...pivotColumns, newPivotColumn];
          updateEditingComponent(comp);
        }}
      >
        Add pivot column
      </FancyButton>
    </>
  );
}

export const manifestETLTablePivot: TpETLComponentTypeManifest = {
  id: 'TablePivot',
  name: 'Pivot Table',
  description: 'Creates a pivot table, turning selected values of a specified source column into separate columns.',
  categoryId: 'table',
  icon: <FontAwesomeIcon icon={['fas', 'table-pivot']} />,
  inputs: [
    {
      type: TpETLObjectTypes.TABLE,
      id: 'sourceTable',
      name: 'Source Table',
      columns: [],
    },
  ],
  outputs: [
    {
      id: 'result',
      name: 'Result table',
      type: TpETLObjectTypes.TABLE,
      description: 'Aggregated table',
      columns: [],
    },
  ],
  params: [
    {
      id: 'groupByColumns',
      name: 'Group by column(s)',
      description:
        'Column(s) used for grouping. In the result table, a separate record will be created for each distinct value of this column(s) in the source table.',
      paramType: createConfigParamTypeExistingColumnList('sourceTable'),
    },
    {
      id: 'dropEmptyValues',
      name: 'Skip Empty Pivot Column Values',
      description: 'If enabled, Rows with empty values in the pivot column will be skipped.',
      paramType: {
        configParamTypeId: CONFIGPARAMTYPES.Boolean,
        defaultValue: false,
        label: 'Skip Empty Values',
      },
    },
    {
      id: 'pivotColumn',
      name: 'Pivot Column',
      description: 'Source column used to create the pivot output columns',
      paramType: createConfigParamTypeExistingColumn('sourceTable'),
    },
    {
      id: 'cellValuesColumn',
      name: 'Cell Values Column',
      description: 'Source column used for the cell values in the pivot table',
      paramType: createConfigParamTypeExistingColumn('sourceTable'),
    },
  ],
  performDryRun,
  renderCustomConfigParams,
};
